import {
  useCallback,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  getSerpSeasonPlanSections,
  SERP_SEASON_PLAN_DETAIL_IDS,
  type SerpSeasonPlanSource,
} from '@/utils/approval/serpSeasonPlan';
import { SerpSeasonPlanRoot } from './styles';

type TablesProps = {
  source: SerpSeasonPlanSource;
  t: TFunction;
  isOpen: (detailId: string) => boolean;
  renderToggle: (detailId: string) => ReactNode;
};

/** 레거시 #APV_SERP_SEASON .form-group 의 표 3개(제목 줄 + 접히는 상세 표) */
const SerpSeasonPlanTables = ({
  source,
  t,
  isOpen,
  renderToggle,
}: TablesProps) => {
  const period = t(
    '(상태 적용일자: {{applyDate}}, 할인 적용기간 : {{startDate}} ~ {{endDate}})',
    {
      applyDate: formatSerpText(source.APLY_DE),
      startDate: formatSerpText(source.APLY_STA_DE),
      endDate: formatSerpText(source.APLY_END_DE),
      interpolation: { escapeValue: false },
    },
  );
  return (
    <div id="lazyLoadArea">
      {getSerpSeasonPlanSections(source).map((section) => {
        const open = isOpen(section.detailId);
        return (
          <section className="mb-20" key={section.detailId}>
            <table className="table table-bordered-horizontal mb-0 text-center">
              <colgroup>
                <col />
                <col className="toggle-col" />
              </colgroup>
              <thead>
                <tr>
                  <th className="bg-light">{`${t(section.title)}${period}`}</th>
                  <th className="bg-light">{renderToggle(section.detailId)}</th>
                </tr>
              </thead>
            </table>
            <div
              id={section.detailId}
              className="table-area"
              data-state={open ? 'open' : 'closed'}
              style={open ? undefined : { display: 'none' }}
            >
              <table className="table table-bordered-horizontal mb-30 text-center season-table">
                <thead>
                  <tr>
                    <td>{t('시즌')}</td>
                    <td>{t('스타일')}</td>
                    <td>{t('컬러')}</td>
                    <td colSpan={3}>{t('설명')}</td>
                    <td>{t('적용단가')}</td>
                    <td>{t('정상가')}</td>
                    <td>{t('할인율')}</td>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td>{formatSerpText(item.SSN_CD)}</td>
                      <td>{formatSerpText(item.PROD_CD)}</td>
                      <td>{formatSerpText(item.COLOR_CD)}</td>
                      <td colSpan={3} className="text-left">
                        {formatSerpText(item.PROD_KO_NM)}
                      </td>
                      <td className="text-right">
                        {formatSerpText(item.DISC_PRC)}
                      </td>
                      <td className="text-right">
                        {formatSerpText(item.SALE_URC)}
                      </td>
                      <td>{formatSerpText(item.DISC_RAT)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={9} className="text-right">
                      {t('총 {{total}} 스타일-컬러', {
                        total: formatSerpText(section.count),
                      })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
};

const ToggleButton = ({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <BoxButton
      type="button"
      variant="secondary"
      size="small"
      aria-expanded={open}
      onClick={onClick}
    >
      {open ? t('상세 닫기') : t('상세 보기')}
    </BoxButton>
  );
};

const useOpenSections = (initial: () => Set<string> = () => new Set()) => {
  const [openIds, setOpenIds] = useState<Set<string>>(initial);
  const toggle = useCallback((detailId: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(detailId)) next.delete(detailId);
      else next.add(detailId);
      return next;
    });
  }, []);
  return { openIds, toggle };
};

/** 작성 화면 — 저장 content 와 같은 표, 접고 펴기는 화면에서만 */
export const SerpSeasonPlanTablesView = ({
  source,
  t,
}: Pick<TablesProps, 'source' | 't'>) => {
  const { openIds, toggle } = useOpenSections();
  return (
    <SerpSeasonPlanRoot>
      <div className="form-group">
        <SerpSeasonPlanTables
          source={source}
          t={t}
          isOpen={(id) => openIds.has(id)}
          renderToggle={(id) => (
            <ToggleButton open={openIds.has(id)} onClick={() => toggle(id)} />
          )}
        />
      </div>
    </SerpSeasonPlanRoot>
  );
};

/** attributes.content — 상세는 닫힌 채로 저장, 버튼은 조회 화면이 data-value 로 찾아 포탈 버튼으로 바꿈 */
export const buildSerpSeasonPlanContentHtml = (
  source: SerpSeasonPlanSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpSeasonPlanTables
        source={source}
        t={t}
        isOpen={() => false}
        renderToggle={(id) => (
          <button type="button" data-value={id}>
            {t('상세 보기')}
          </button>
        )}
      />
    </div>,
  );

/** 레거시 문서는 저장 당시 펼쳐 둔 상태(data-state)로 시작 */
const readSavedOpenIds = (content: string): Set<string> => {
  const open = new Set<string>();
  if (typeof DOMParser === 'undefined') return open;
  const doc = new DOMParser().parseFromString(content, 'text/html');
  SERP_SEASON_PLAN_DETAIL_IDS.forEach((id) => {
    if (doc.getElementById(id)?.getAttribute('data-state') === 'open') {
      open.add(id);
    }
  });
  return open;
};

/** 저장 content(레거시 문서 포함) — 「상세 보기」 버튼만 포탈 버튼으로 바꿔 접고 펴기 */
export const SerpSeasonPlanSnapshot = ({ content }: { content: string }) => {
  const { openIds, toggle } = useOpenSections(() => readSavedOpenIds(content));
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const detailId = element.getAttribute('data-value');
      if (tagName === 'button' && detailId) {
        return (
          <ToggleButton
            key={props.key as string}
            open={openIds.has(detailId)}
            onClick={() => toggle(detailId)}
          />
        );
      }
      // 레거시 행의 display:table 은 버려지는 <style> 의 고정 칸 너비와 짝 — 남기면 행마다 칸이 어긋남
      if (tagName === 'tr' && props.style) {
        const {
          display: _display,
          width: _width,
          ...rest
        } = props.style as CSSProperties;
        props.style = rest;
      }
      if (SERP_SEASON_PLAN_DETAIL_IDS.has(element.id)) {
        const { display: _, ...rest } = (props.style ?? {}) as CSSProperties;
        props.style = openIds.has(element.id)
          ? rest
          : { ...rest, display: 'none' };
      }
      return undefined;
    },
    [openIds, toggle],
  );
  return (
    <SerpSeasonPlanRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </SerpSeasonPlanRoot>
  );
};
